import React, { useEffect, useRef, useState } from 'react';
import { SITE_CONTENT } from '../content';

/* ── Animated counter hook ── */
function useCountUp(target, duration = 1800, started = false, decimal = false) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!started) return;

    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * target;
      setValue(decimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [started, target, duration, decimal]);

  return value;
}

/* ── Individual stat item ── */
function StatItem({ end, suffix, label, decimal, started, isLast }) {
  const count = useCountUp(end, 1800, started, decimal);

  return (
    <div
      className={`flex flex-col items-center justify-center py-8 px-6 lg:px-10 flex-1 ${
        !isLast ? 'border-r border-[#d4cfc5]' : ''
      }`}
    >
      <div className="flex items-start gap-0.5 mb-2">
        <span className="text-[#1a2e1a] text-4xl lg:text-5xl font-semibold tracking-tight leading-none">
          {decimal ? count.toFixed(1) : count}
        </span>
        <span className="text-[#1a2e1a] text-md font-medium mt-1 ml-0.5 leading-none">
          {suffix}
        </span>
      </div>
      <p className="text-[#7a7a68] text-md tracking-wide">{label}</p>
    </div>
  );
}

/* ── Main section ── */
const StatsSection = () => {
  const stats = SITE_CONTENT.pages?.aboutUsPage?.statsSection?.stats || [];
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-[#f0ebe0] py-6 lg:py-10" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Bordered card */}
        <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 border border-[#d4cfc5] rounded-sm bg-[#f0ebe0]">
          {stats.map((stat, idx) => (
            <StatItem
              key={stat.label}
              {...stat}
              started={started}
              isLast={idx === stats.length - 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
